import React, {ReactElement} from "react";
import {InView} from "react-intersection-observer";

import {css} from "/src/utils/utils";

import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import Video, {VideoType} from "../videos/video/Video";
import ModuleBody from "../layouts/module-body/ModuleBody";
import SimpleFormWrapper from "../forms/simple-form-wrapper/SimpleFormWrapper";
import MultiForm from "../forms/multi-form/MultiForm";
import MarkdownText from "../markdown/markdown-text/MarkdownText";
import Image, {ImageObject} from "../image/Image";
import Quote from "../cards/quote/Quote";
import LinkCollection from "../link-collection/LinkCollection";
import {MaybeType, MultiFormProps, SimpleFormProps} from "/src/types";
import ReactPlayer from "react-player";

import * as styles from "./split-module.module.scss";

interface StatCardProps {
  headline: string;
  copy: string;
  darkMode?: boolean;
  classes?: string;
}

const StatCard = (props: StatCardProps): ReactElement => {
  const {headline, copy, darkMode} = props;

  return (
    <div className={css(styles.statCard, darkMode ? styles.statCardDark : "")}>
      {headline && (
        <h3 className={css("h3", styles.statCard__heading)}>{headline}</h3>
      )}

      {copy && <p className={css("p", styles.statCard__copy)}>{copy}</p>}
    </div>
  );
};

export interface SplitModuleProps
  extends Omit<
    Queries.SplitModuleFragmentFragment,
    | "image"
    | "videoPreviewImage"
    | "mediaSpaceAllocation"
    | "imageAlignment"
    | "form"
    | "videoBehavior"
  > {
  mediaSpaceAllocation?: "50%" | "75%";
  imageAlignment?: "left" | "right";
  videoBehavior?: VideoType;
  form?: SimpleFormProps | MultiFormProps | null;
  image?: MaybeType<ImageObject>;
  videoPreviewImage?: MaybeType<ImageObject>;
}

const SplitModule = (props: SplitModuleProps): ReactElement | null => {
  const {
    image,
    copyModule,
    extraCopyModule,
    mediaSpaceAllocation,
    imageAlignment,
    darkMode,
    videoSource,
    videoPreviewImage,
    videoBehavior = VideoType.autoplay,
    form,
    fastFacts,
    title,
    videoControls,
    quote,
    listOfLinks,
    anchorLinkId,
  } = props;

  const videoRef = React.useRef<ReactPlayer | null>(null);
  const [playVideo, setPlayVideo] = React.useState(false);

  const hasVideo = videoSource != null;

  let contentForm = null;
  // if there is form, and type === simple form || type multiform
  if (form != null) {
    if (form.__typename === "ContentfulSimpleForm") {
      contentForm = <SimpleFormWrapper {...form} />;
    } else if (form.__typename === "ContentfulMultiStepForm") {
      contentForm = <MultiForm {...form} />;
    }
  }

  // content coming from copy module
  const cta = copyModule?.ctaButton;
  const eyebrow = copyModule?.eyebrow!;
  const headline = copyModule?.title!;
  const copy = copyModule?.body?.body!;

  // content coming from extra copy module
  const extraEyebrow = extraCopyModule?.eyebrow!;
  const extraHeadline = extraCopyModule?.title!;
  const extraCopy = extraCopyModule?.body?.body!;

  // content coming from fast fact
  const leftHeadline = fastFacts?.leftHeadline!;
  const leftCopy =
    fastFacts?.leftBodyCopy?.leftBodyCopy || fastFacts?.leftCopy!;
  const middleHeadline = fastFacts?.middleHeadline!;
  const middleCopy =
    fastFacts?.middleBodyCopy?.middleBodyCopy || fastFacts?.middleCopy!;
  const rightHeadline = fastFacts?.rightHeadline!;
  const rightCopy =
    fastFacts?.rightBodyCopy?.rightBodyCopy || fastFacts?.rightCopy!;

  const isDarkMode: boolean = darkMode === true;

  return (
    <section
      className={css(
        styles.splitModule,
        darkMode ? styles.splitModuleDark : "",
        mediaSpaceAllocation === "75%" ? styles.splitModuleMedia75 : "",
        imageAlignment === "right" ? styles.splitModuleImageRight : ""
      )}
      {...(anchorLinkId ? {id: anchorLinkId} : {})}
    >
      <ModuleBody>
        {title && (
          <h2 className={css("h2", styles.splitModule__title)}>{title}</h2>
        )}
        <div className={styles.splitModule__contentWrapper}>
          {hasVideo && (
            <div className={styles.splitModule__videoWrapper}>
              <InView
                as="div"
                className={styles.splitModule__videoInview}
                onChange={inView => {
                  setPlayVideo(inView);
                }}
              >
                <Video
                  videoConfig={{
                    wistia: {
                      options: {
                        fitStrategy: "contain",
                      },
                    },
                  }}
                  showControl={videoControls === true}
                  videoClasses={styles.splitModule__video}
                  videoTitle={headline}
                  type={videoBehavior}
                  videoThumbnail={videoPreviewImage as ImageObject}
                  videoSource={videoSource}
                  videoRef={videoRef}
                  playing={playVideo}
                />
              </InView>
            </div>
          )}

          {image && !hasVideo && (
            <div className={styles.splitModule__imgWrapper}>
              <Image
                classes={styles.splitModule__img}
                image={image as ImageObject}
              />
            </div>
          )}

          {listOfLinks && (
            <div className={styles.splitModule__linksWrapper}>
              <LinkCollection singleColumnLayout={true} {...listOfLinks} />
            </div>
          )}

          <div className={styles.splitModule__content}>
            {eyebrow && (
              <p
                className={css("eyebrow", styles.splitModule__content__eyebrow)}
              >
                {eyebrow}
              </p>
            )}

            {headline && (
              <h2 className={css("h2", styles.splitModule__content__headline)}>
                {headline}
              </h2>
            )}

            {copy && (
              <MarkdownText
                classes={styles.splitModule__content__copy}
                text={copy}
              />
            )}

            {extraEyebrow && (
              <p
                className={css("eyebrow", styles.splitModule__content__eyebrow)}
              >
                {extraEyebrow}
              </p>
            )}

            {extraHeadline && (
              <h2 className={css("h2", styles.splitModule__content__headline)}>
                {extraHeadline}
              </h2>
            )}

            {extraCopy && (
              <MarkdownText
                classes={styles.splitModule__content__copy}
                text={extraCopy}
              />
            )}

            {quote != null && (
              <Quote
                darkMode={darkMode === true}
                image={quote.image as ImageObject}
                name={quote.name!}
                quote={quote.quote!}
                role={quote.role!}
              />
            )}

            {cta && (
              <CTAButton
                classes={styles.splitModule__content__cta}
                kind="secondary"
                cta={cta as CTA}
                darkMode={isDarkMode}
              />
            )}

            {contentForm === null && (
              <>
                {leftHeadline && leftCopy && (
                  <StatCard
                    headline={leftHeadline}
                    copy={leftCopy}
                    darkMode={isDarkMode}
                  />
                )}

                {middleHeadline && middleCopy && (
                  <StatCard
                    headline={middleHeadline}
                    copy={middleCopy}
                    darkMode={isDarkMode}
                  />
                )}

                {rightHeadline && rightCopy && (
                  <StatCard
                    headline={rightHeadline}
                    copy={rightCopy}
                    darkMode={isDarkMode}
                  />
                )}
              </>
            )}

            {mediaSpaceAllocation === "50%" && contentForm && (
              <>{contentForm}</>
            )}
          </div>
        </div>
      </ModuleBody>
    </section>
  );
};

export default SplitModule;
